import { LinearProgress, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import TableSchemata from "components/base/TableSchemata";
import { Section } from "components/disw";
import { ErrorBox } from "components/error";
import { lazy, Suspense } from "react";
import DataSourceSpecDownload, {
  useDataSourceSpecDownload
} from "./DataSourceSpecDownload";
import { useDataSourceVersion } from "./hooks/useDataSources";

// Lazy, so swagger-ui is not loaded with the route table
const DataSourceApiTester = lazy(() => import("./DataSourceApiTester"));

const useStyles = makeStyles({
  specification: { overflowX: "auto", minWidth: 0 }
});

/** Schemata and, for live versions, API tester sections of a data source version. */
export default function DataSourceVersionContent({
  solutionKey,
  dataSourceKey,
  version,
  isLive
}: {
  solutionKey: string;
  dataSourceKey: string;
  version: string;
  isLive: boolean;
}) {
  const classes = useStyles();
  const { data, loading, error } = useDataSourceVersion(
    solutionKey,
    dataSourceKey,
    version
  );
  const details =
    data?.solutions.solution?.cloudDataSources?.dataSource?.version;
  const specDownload = useDataSourceSpecDownload(
    solutionKey,
    dataSourceKey,
    version
  );

  if (loading) return <LinearProgress />;
  if (error) return <ErrorBox apolloError={error} />;
  if (!details)
    return <Typography>Version {version} is not available.</Typography>;

  return (
    <>
      <Section name="Schemata">
        <TableSchemata schema={details.schema} />
      </Section>
      {isLive && (
        <Section
          name="API tester"
          actions={
            details.openApiSpec && (
              <DataSourceSpecDownload
                onDownload={specDownload.download}
                disabled={specDownload.loading}
              />
            )
          }
        >
          {specDownload.loading && <LinearProgress />}
          {specDownload.error && (
            <ErrorBox
              apolloError={specDownload.error}
              title="Unable to download the OpenAPI specification"
            />
          )}
          {details.openApiSpec ? (
            <div
              className={classes.specification}
              data-testid="data-source-swagger"
            >
              <Suspense fallback={<LinearProgress />}>
                <DataSourceApiTester
                  key={`${dataSourceKey}/${version}`}
                  spec={details.openApiSpec}
                />
              </Suspense>
            </div>
          ) : (
            <Typography variant="body2">
              No OpenAPI specification available for this version.
            </Typography>
          )}
        </Section>
      )}
    </>
  );
}
